// Room Status Guard Hook returning a gate for the current room status

import type { ReactNode } from 'react';
import {
  TransitionMessage,
  ROOM_LIFECYCLE,
  ERRORS,
} from '../components/transitionMessage';
import { useRoomConfig } from './useRoomConfig';
import { getGameCodeFolder } from '@airhead-games/shared/utilities';

type RoomStatusGuardResult = {
  roomCode: string | undefined;
  gameId: string | undefined;
  status: ReturnType<typeof useRoomConfig>['status'];
  gate: ReactNode | null;
};

export const useRoomStatusGuard = (): RoomStatusGuardResult => {
  const { roomCode, gameId, status } = useRoomConfig();

  if (status === undefined) {
    return {
      roomCode,
      gameId,
      status,
      gate: (
        <TransitionMessage
          messages={ROOM_LIFECYCLE.connectingToRoom}
          showSpinner
          enableFunnyMessages
        />
      ),
    };
  }

  if (status === 'opened') {
    return {
      roomCode,
      gameId,
      status,
      gate: <TransitionMessage messages={ROOM_LIFECYCLE.waitingForGameStart} />,
    };
  }

  if (status === 'closed') {
    return {
      roomCode,
      gameId,
      status,
      gate: <TransitionMessage messages={ROOM_LIFECYCLE.roomClosed} />,
    };
  }

  if (gameId && !getGameCodeFolder(gameId)) {
    return {
      roomCode,
      gameId,
      status,
      gate: <TransitionMessage messages={ERRORS.unableToLoadGame} />,
    };
  }

  return {
    roomCode,
    gameId,
    status,
    gate: null,
  };
};
